import { BlogPost, BLOG_AUTHOR } from '../../../types/blogTypes';

const post: BlogPost = {
  slug: 'dinner-from-whatever-is-in-your-fridge',
  title: 'What to Cook With the Ingredients You Have: A 4-Part Formula',
  description:
    'Wondering what to cook with ingredients you have? Use this four-part dinner formula, a short pantry list and Marco, the Chef buddy, to plan tonight.',
  category: 'Everyday Life',
  icon: 'utensils',
  publishedAt: '2026-09-01',
  author: BLOG_AUTHOR,
  product: 'chot-buddies',
  buddy: 'Marco',
  keywords: [
    'what to cook with ingredients i have',
    'dinner ideas from leftovers',
    'cooking with pantry staples',
    'ai chef',
  ],
  body: `It's 6:07pm. You open the fridge and find half an onion, two eggs, a bunch of cilantro on its way out, leftover rice and a jar you bought for one recipe back in March. You close the door. You open it again, as if the contents might have rearranged themselves. Then you order takeout.

If you have ever typed "what to cook with ingredients I have" into a search bar at that hour, this post gives you a plan to borrow. Most "there's nothing to eat" nights are a shortage of plan, not food.

## The four-part dinner formula

Most satisfying dinners, from fried rice to a sheet-pan supper, use the same four pieces:

1. **A protein.** Eggs, beans, lentils, tofu, canned fish, chicken, sausage, last night's leftovers.
2. **A vegetable.** Fresh, frozen or a little sad. Soft vegetables are still great roasted, sauteed or blended into a sauce.
3. **A carb or base.** Rice, pasta, noodles, potatoes, tortillas, toast, couscous.
4. **Something for flavor, acid or crunch.** Lemon, vinegar, hot sauce, fresh herbs, pickles, cheese, toasted nuts or seeds.

Pick one from each and you have dinner. Eggs, wilted spinach, leftover rice and a squeeze of lemon becomes a fried rice bowl. Canned chickpeas, a roasted sweet potato, a spoonful of yogurt and chili flakes becomes a warm grain-free bowl.

The fourth piece is the one people skip, and it makes food taste like someone cooked it on purpose. A squeeze of acid or a handful of crunch at the end rescues a plain plate.

For a quick weeknight order of operations: start the base first, cook the protein and vegetable together in one pan, and add the bright piece off the heat. Taste before you serve.

## Pantry staples that make almost anything work

You do not need a fancy pantry. You need a small, boring one that you restock:

- Cooking oil, salt and black pepper
- Onions and garlic
- Dried pasta and rice
- Canned tomatoes and canned beans
- Eggs
- Stock or bouillon
- Soy sauce and a vinegar or two
- A few spices you like, such as paprika, cumin and chili flakes
- A bag of frozen vegetables (peas, spinach and corn are forgiving)

With those on the shelf, a fridge with almost nothing in it still holds the beginnings of dinner. Frozen vegetables count as vegetables, and canned beans count as protein.

## Smart substitutions when you are missing one thing

A missing ingredient rarely means a ruined recipe. A few swaps that usually work:

- **No lemon:** a small splash of vinegar. Start with less than you think you need.
- **No fresh herbs:** use dried, but use less and add them earlier so they have time to soften.
- **No cream:** a splash of milk with a bit of butter, or a spoonful of yogurt stirred in off the heat.
- **No chicken:** chickpeas, white beans, eggs or tofu carry most of the same dishes.
- **No pasta:** rice, couscous, noodles or even torn-up toast can play the base.
- **No shallot:** a regular onion, chopped finer and cooked a touch longer.

One honest caution: if anyone eating has an allergy or a dietary restriction, check every swap and every label before you cook. "Close enough" is not good enough there.

## How to rescue a dish that went wrong

Everyone has watched dinner go sideways. Most of it is fixable.

- **Too salty:** dilute it. Add more of the unsalted parts, like extra rice, pasta, beans, potatoes or a splash of water or unsalted stock. A squeeze of acid or a spoonful of something creamy also softens the edge.
- **Too bland:** salt first, then taste. Still flat? Add acid (lemon, vinegar), then something savory like soy sauce, tomato paste or grated cheese.
- **Too watery:** simmer it uncovered to cook the liquid off, or mash a few beans or potatoes into it to thicken. Or serve it over rice or toast and call it a stew.
- **Overcooked:** mushy vegetables can be blended into a soup, dry chicken can be shredded and tossed in sauce, and soft pasta makes a decent baked dish with cheese on top.
- **Scorched on the bottom:** do not scrape. Move the good food to a clean pan and leave the burnt layer behind.

## Cooking for one, or for a whole table

**For one**, think in components rather than recipes. Cook a pot of rice and use half. Roast a tray of vegetables and eat them three ways. Eggs are the easiest single-serving protein.

**For a family**, build dinner so everyone can customize it: tacos, bowls, baked potatoes, pasta with a few toppings. Cook one big base, then let people add their own extras. When you scale a recipe up, double the base first and season gradually, because salt and spice do not always scale in a straight line.

Either way, cool leftovers and get them into the fridge promptly. For specific storage times and temperatures, check your national food safety agency rather than guessing.

## What to cook with ingredients you have: ask Marco

The formula works. At 6pm the hard part is the thinking, so hand that to Marco. Marco is the Chef buddy in [Chot Buddies](/products/chot-buddies/), built around recipes, techniques and a love of cooking. He is one of a whole cast of specialists, and [there is a reason a team beats one generic chatbot](/blog/why-a-team-of-specialists-beats-one-chatbot/).

To get a useful answer, give him four things:

- **What you have.** Be specific, including the sad stuff.
- **Your time limit.** "20 minutes" changes the whole menu.
- **Your equipment.** One pan? No oven? A microwave and a kettle? Say so.
- **Your skill level.** "I can boil pasta and not much else" is a perfectly good answer.

Here are three prompts you can paste in as they are:

> I have chicken thighs, half a cabbage, rice, an onion, soy sauce and one lemon. I have 25 minutes, one big pan and a rice cooker, and I'm a confident beginner. What can I make for two?

> It's only me tonight. I have eggs, a bag of spinach that's starting to wilt, feta and some stale bread. No oven. Give me one dinner, and tell me what to do with the rest of the feta.

> I made a tomato sauce and it's way too salty. It's still on the stove. Walk me through fixing it step by step, and tell me what to do differently next time.

Hands covered in flour? Try [talking instead of typing](/blog/talk-dont-type-voice-mode/). Voice mode is made for exactly that moment.

## Tell Marco about allergies, then double-check the big stuff

Always mention allergies, intolerances and dietary needs at the start of the conversation, whether that is nuts, gluten, dairy, shellfish or something religious or personal. Marco can plan around them as you swap ingredients.

But Marco is a cooking companion, not a medical or food-safety authority. For anything involving allergens, safe internal temperatures, how long something has been in the fridge, or whether a food is safe to eat, double-check with reliable official sources, such as your national food safety agency, the product label or your doctor.

## Tonight's small win

You do not need a perfect fridge or a perfect recipe. You need one protein, one vegetable, one base and something bright on top.

Tonight, open the fridge once and list what you see. Send Marco that list, your time limit and your equipment, and ask for one dinner. Want fewer 6pm scrambles? [The 10-Minute Sunday Reset](/blog/the-10-minute-sunday-reset/) sets up your week in one sitting.`,
};

export default post;
